import { useState, useEffect } from 'react';
import { DatePicker, Radio, Select } from 'antd';
import styled from 'styled-components';
import dayjs from 'dayjs';
const { Option } = Select;

const { RangePicker } = DatePicker;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 16px;

  .ant-radio-group {
    display: flex;
    flex-wrap: nowrap;
    gap: 4px;
  }

  .ant-radio-button-wrapper {
    padding: 0 10px;
    white-space: nowrap;
  }
`;

const parseRangeValue = (value) => {
  if (!value?.startsWith('range:')) return null;
  const parts = value.slice(6).split('-');
  if (parts.length !== 2) return null;
  return [dayjs(parts[0], 'DD.MM.YYYY'), dayjs(parts[1], 'DD.MM.YYYY')];
};

const formatRangeString = (dates) => {
  return `range:${dates[0].format('DD.MM.YYYY')}-${dates[1].format('DD.MM.YYYY')}`;
};

const DtRangeSelector = ({ value, onChange, disabled }) => {
  const currentYear = new Date().getFullYear();
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedCustom, setSelectedCustom] = useState(null);

  const isRange = value?.startsWith('range:');
  const rangeDates = isRange ? parseRangeValue(value) : null;

  const handleYearSelect = (val) => {
    setSelectedYear(val);
    setSelectedCustom(null);
    setShowRangePicker(false);

    if (selectedMonth) {
      onChange?.(`ym-${val}-${selectedMonth}`);
    } else {
      onChange?.(val);
    }
  };

  const handleMonthSelect = (val) => {
    const year = selectedYear || currentYear.toString();
    setSelectedYear(year);
    setSelectedMonth(val);
    setSelectedCustom(null);
    setShowRangePicker(false);
    console.log("handling month select", val)

    if (val == undefined){
      onChange?.(`${year}`);
    } else {
      onChange?.(`ym-${year}-${val}`);
    }

  };

  const handleCustomSelect = (val) => {
    setSelectedCustom(val);
    setSelectedMonth(null);
    setSelectedYear(null);
    setShowRangePicker(val === 'range');

    if (val !== 'range') {
      onChange?.(val);
    }
  };

  const handleRangeChange = (dates) => {
    if (dates?.length === 2) {
      const formatted = formatRangeString(dates);
      onChange?.(formatted);
    }
  };

  useEffect(() => {
    if (!value) {
      // Reset all local state when value is cleared
      setSelectedYear(null);
      setSelectedMonth(null);
      setSelectedCustom(null);
      setShowRangePicker(false);
      return;
    }

    // Handle range
    if (value.startsWith("range:")) {
      setSelectedYear(null);
      setSelectedMonth(null);
      setSelectedCustom("range");
      setShowRangePicker(true);
      return;
    }

    // Handle full year
    if (/^\d{4}$/.test(value)) {
      setSelectedYear(value);
      setSelectedMonth(null);
      setSelectedCustom(null);
      setShowRangePicker(false);
      return;
    }

    // Handle ym-YYYY-MM
    if (/^ym-\d{4}-\d{2}$/.test(value)) {
      const [_, y, m] = value.split("-");
      setSelectedYear(y);
      setSelectedMonth(m);
      setSelectedCustom(null);
      setShowRangePicker(false);
      return;
    }

    // Handle custom quick presets (-6m, -3m, etc.)
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedCustom(value);
    setShowRangePicker(value === "range");
  }, [value]);

  return (
    <Wrapper>
      <Radio.Group
        size="small"
        buttonStyle="solid"
        value={selectedYear}
        onChange={(e) => handleYearSelect(e.target.value)}
        disabled={disabled}
      >
        {[...Array(4)].map((_, index) => {
          const year = new Date().getFullYear() - (2 - index); // -2, -1, current, +1
          return (
            <Radio.Button key={year} value={year.toString()}>
              {year}
            </Radio.Button>
          );
        })}
      </Radio.Group>

      {/* Month select is only relevant when not picking a custom range.
          A custom range clears the year/month selection, so we swap it out
          for the range picker to keep everything on one line. */}
      {!showRangePicker && (
        <Select
          size="small"
          value={selectedMonth}
          onChange={handleMonthSelect}
          style={{ width: 120 }}
          allowClear
          placeholder="Celo leto"
          disabled={disabled}
        >
          <Option value="">CELO LETO</Option>
          <Option value="01">JAN</Option>
          <Option value="02">FEB</Option>
          <Option value="03">MAR</Option>
          <Option value="04">APR</Option>
          <Option value="05">MAJ</Option>
          <Option value="06">JUN</Option>
          <Option value="07">JUL</Option>
          <Option value="08">AUG</Option>
          <Option value="09">SEP</Option>
          <Option value="10">OKT</Option>
          <Option value="11">NOV</Option>
          <Option value="12">DEC</Option>
        </Select>
      )}

      <Select
        placeholder='Izberi obdobje'
        size="small"
        value={selectedCustom}
        onChange={(value) => handleCustomSelect(value)}
        style={{ width: 120 }}
        disabled={disabled}
        options={[
          { value: "ytd", label: "YTD" },
          { value: "-3m", label: "-3M" },
          { value: "M", label: "M" },
          { value: "-7d", label: "-7D" },
          { value: "past_not_today", label: "< Danes" },
          { value: "today", label: "Danes" },
          { value: "+7d", label: "+7D" },
          { value: "+3m", label: "+3M" },
          { value: "range", label: "Obdobje" },
        ]}
      />

      {showRangePicker && (
        <RangePicker
          value={rangeDates}
          onChange={handleRangeChange}
          allowClear={false}
          format="DD.MM.YYYY"
          size="small"
          style={{ minWidth: 240 }}
          disabled={disabled}
        />
      )}
    </Wrapper>
  );
};

export default DtRangeSelector;
